import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, eur, kindLabel, statusLabel } from "@/lib/api";
import AdminHeader from "@/components/AdminHeader";
import { toast } from "sonner";

const STATUS_TABS = [
  { id: "", label: "Toutes" },
  { id: "awaiting_validation", label: "À valider" },
  { id: "confirmed", label: "Confirmées" },
  { id: "completed", label: "Terminées" },
  { id: "refused", label: "Refusées" },
  { id: "pending_payment", label: "Non payées" },
];

export default function AdminDashboard() {
  const [status, setStatus] = useState("");
  const [type, setType] = useState("");
  const [rows, setRows] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const nav = useNavigate();

  useEffect(() => {
    api.get("/admin/me").catch(() => nav("/admin"));
  }, [nav]);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (status) params.status = status;
      if (type) params.booking_type = type;
      const [b, s] = await Promise.all([
        api.get("/admin/bookings", { params }),
        api.get("/admin/stats"),
      ]);
      setRows(b.data.bookings);
      setStats(s.data);
    } catch (e) {
      toast.error("Chargement impossible");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, [status, type]);

  return (
    <div className="min-h-screen bg-[#050505]" data-testid="admin-dashboard">
      <AdminHeader />
      <main className="max-w-7xl mx-auto pt-28 pb-16 px-6 lg:px-10">
        <div className="flex items-baseline justify-between flex-wrap gap-4">
          <div>
            <div className="text-xs tracking-[0.5em] gold uppercase">Tableau de bord</div>
            <h1 className="mt-2 font-serif text-white text-4xl">Réservations</h1>
          </div>
        </div>

        {/* Stats */}
        {stats && (
          <div className="mt-8 grid grid-cols-2 lg:grid-cols-5 gap-4">
            <StatCard label="À valider" value={stats.awaiting_validation} accent testId="stat-awaiting" />
            <StatCard label="Confirmées" value={stats.confirmed} testId="stat-confirmed" />
            <StatCard label="Terminées" value={stats.completed} testId="stat-completed" />
            <StatCard label="Refusées" value={stats.refused} testId="stat-refused" />
            <StatCard label="CA encaissé" value={eur(stats.revenue_paid)} testId="stat-revenue" />
          </div>
        )}

        {/* Filters */}
        <div className="mt-10 flex flex-wrap gap-2 items-center">
          <div className="flex flex-wrap gap-2">
            {STATUS_TABS.map((t) => (
              <button
                key={t.id || "all"}
                data-testid={`filter-status-${t.id || "all"}`}
                onClick={() => setStatus(t.id)}
                className={`text-xs px-4 py-2 border tracking-wide transition-all ${
                  status === t.id
                    ? "bg-[color:var(--gold)] text-black border-[color:var(--gold)]"
                    : "border-white/10 text-gray-400 hover:border-white/30"
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>
          <div className="ml-auto">
            <select
              data-testid="filter-type"
              value={type}
              onChange={(e) => setType(e.target.value)}
              className="bg-black border border-white/10 text-gray-300 px-3 py-2 text-sm"
            >
              <option value="">Tous les services</option>
              <option value="convoyage">Convoyage</option>
              <option value="vtc_trip">Trajet VTC</option>
              <option value="vtc_dispo">Mise à disposition</option>
            </select>
          </div>
        </div>

        {/* Table */}
        <div className="mt-6 border border-white/10 bg-[#0a0a0a] overflow-x-auto">
          <table className="w-full text-sm" data-testid="admin-bookings-table">
            <thead>
              <tr className="text-left text-xs uppercase tracking-widest text-gray-500">
                <th className="p-4">Référence</th>
                <th className="p-4">Type</th>
                <th className="p-4">Client</th>
                <th className="p-4">Montant</th>
                <th className="p-4">Paiement</th>
                <th className="p-4">Statut</th>
                <th className="p-4">Date</th>
                <th className="p-4"></th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td colSpan={8} className="p-6 text-center text-gray-500">Chargement...</td>
                </tr>
              )}
              {!loading && rows.length === 0 && (
                <tr>
                  <td colSpan={8} className="p-6 text-center text-gray-500">Aucune réservation.</td>
                </tr>
              )}
              {rows.map((b) => (
                <tr key={b.id} className="border-t border-white/5 hover:bg-white/[0.02] transition-colors">
                  <td className="p-4 gold tracking-widest" data-testid={`row-ref-${b.reference}`}>{b.reference}</td>
                  <td className="p-4 text-gray-300">{kindLabel(b.booking_type)}</td>
                  <td className="p-4 text-gray-300">
                    <div>{b.client.full_name}</div>
                    <div className="text-xs text-gray-500">{b.client.email}</div>
                  </td>
                  <td className="p-4 text-white">{eur(b.price.total)}</td>
                  <td className="p-4 text-gray-300">{statusLabel(b.payment_status)}</td>
                  <td className="p-4">
                    <StatusBadge status={b.status} />
                  </td>
                  <td className="p-4 text-gray-500 text-xs">{new Date(b.created_at).toLocaleString("fr-FR")}</td>
                  <td className="p-4 text-right">
                    <Link
                      to={`/admin/bookings/${b.id}`}
                      data-testid={`row-open-${b.reference}`}
                      className="text-xs gold hover:underline"
                    >
                      Détails →
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </main>
    </div>
  );
}

function StatCard({ label, value, accent, testId }) {
  return (
    <div
      data-testid={testId}
      className={`p-5 border ${
        accent ? "border-[color:var(--gold)]/40 bg-[color:var(--gold)]/[0.03]" : "border-white/10 bg-[#0a0a0a]"
      }`}
    >
      <div className="text-[10px] uppercase tracking-widest text-gray-500">{label}</div>
      <div className={`mt-2 font-serif text-3xl ${accent ? "gold" : "text-white"}`}>{value}</div>
    </div>
  );
}

export function StatusBadge({ status }) {
  const styles = {
    pending_payment: "border-gray-500/40 text-gray-400",
    awaiting_validation: "border-yellow-500/40 text-yellow-300 bg-yellow-500/5",
    confirmed: "border-emerald-500/40 text-emerald-300 bg-emerald-500/5",
    refused: "border-red-500/40 text-red-300 bg-red-500/5",
    completed: "border-[color:var(--gold)]/40 gold bg-[color:var(--gold)]/5",
    in_progress: "border-blue-500/40 text-blue-300",
    cancelled: "border-gray-500/40 text-gray-500",
  };
  return (
    <span className={`inline-block px-2.5 py-1 text-[10px] uppercase tracking-widest border ${styles[status] || "border-white/10 text-gray-300"}`}>
      {statusLabel(status)}
    </span>
  );
}
